import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonButtonRowWidget, UtilityService } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { PersonWebLink, WeblinktypeId } from '@interfaces';
import { StaticDataService } from '../../../../services';
import { PersonExplorerService } from '../person-explorer.service';
import { getAddressEditorDialogData, getContactMethodEditorDialogData, getDoNotContactEditorDialogData, getEmailEditorDialogData, getNameEditorDialogData, getPhoneEditorDialogData, getWebLinkEditorDialogData } from './person-basic-info-dialogs-data';


@Component({
	selector: 'person-basic-info-part',
	standalone: true,
	imports: [
		CommonButtonRowWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './person-basic-info.part.html',
	styleUrl: './person-basic-info.part.scss'
})

export class PersonBasicInfoPart {

	#util = inject(UtilityService);
	#dialogService = inject(DialogService);
	#staticDataService = inject(StaticDataService);
	personExplorer = inject(PersonExplorerService);

	linkIconByType: Record<number, string> = {
		[WeblinktypeId.Website]: 'fa-regular fa-link',
		[WeblinktypeId.Facebook]: 'fa-brands fa-facebook',
		[WeblinktypeId.Linkedin]: 'fa-brands fa-linkedin',
		[WeblinktypeId.Twitter]: 'fa-brands fa-twitter',
	};

	emailErrorMsg = signal<string | undefined>(undefined);

	person = computed(() => {
		const person = this.personExplorer.person.value();
		if (!person) return undefined;

		return {
			...person,
			otherEmails: person.general.otherEmails.filter(email => !email.deleted),
			contactMethod: person.general.contactMethodId ? this.#staticDataService.contact_methods.byId(person.general.contactMethodId).name : 'N/A',
			phoneType: person.general.phoneTypeId ? this.#staticDataService.phonetypes.byId(person.general.phoneTypeId).name : 'N/A',
			phone: person.general.phone ? this.#util.phone.format(person.general.phone) : 'N/A',
		}
	});

	async openNameEditor() {
		const person = this.person();
		if (!person) return;

		const dialogData = getNameEditorDialogData(person, this.personExplorer);
		await this.#dialogService.showAngularForm(dialogData, 400, 400);
	}

	async openAddressEditor() {
		const person = this.person();
		if (!person) return;

		const dialogData = getAddressEditorDialogData(person, this.#util, this.personExplorer);
		await this.#dialogService.showAngularForm(dialogData, 400, 250);
	}

	async openContactMethodEditor() {
		const person = this.person();
		if (!person) return;

		const contactMethodOptions = computed(() =>
			this.#staticDataService.contact_methods.all
				.filter(type => type.canBeDefaultContactMethod)
				.map(type => ({ value: type.id, text: type.name }))
		);

		const dialogData = getContactMethodEditorDialogData(person, this.personExplorer, contactMethodOptions);
		await this.#dialogService.showAngularForm(dialogData, 400, 250);
	}

	async openPhoneEditor() {
		const person = this.person();
		if (!person) return;

		const phoneTypeOptions = computed(() => this.#staticDataService.phonetypes.all.map(type => ({ value: type.id, text: type.name })));

		const dialogData = getPhoneEditorDialogData(person, this.personExplorer, phoneTypeOptions);
		await this.#dialogService.showAngularForm(dialogData, 400, 300);
	}

	async editDoNotContact() {
		const person = this.person();
		if (!person) return;

		const dialogData = getDoNotContactEditorDialogData(person, this.personExplorer);
		await this.#dialogService.showAngularForm(dialogData, 400, 300);
	}


	async addEmail() {
		this.emailErrorMsg.set(undefined);
		const dialogData = getEmailEditorDialogData(this.personExplorer, this.emailErrorMsg);
		await this.#dialogService.showAngularForm(dialogData, 400, 250);
	}

	async setPrimaryEmail(emailId: number, email: string) {
		const yes = await this.#dialogService.confirm(`Are you sure you want to set ${email} as primary?`, 'Yes', 'No', [], 400, 200);
		if (yes) {
			await this.personExplorer.setPrimaryEmail(emailId);
		}
	}

	async deleteEmail(emailId: number, email: string) {
		const yes = await this.#dialogService.confirm(`Do you want to remove ${email} from ${this.person()?.general.fullName} ?`, 'Yes', 'No', [], 400, 200);
		if (yes) {
			await this.personExplorer.deleteEmail(emailId);
		}
	}


	async addOrUpdateWebLink(webLink?: PersonWebLink) {
		const webLinkOptions = computed(() => this.#staticDataService.weblinktypes.all.map(type => ({ value: type.id, text: type.name, icon: this.linkIconByType[type.id] })));

		const dialogData = getWebLinkEditorDialogData(this.personExplorer, webLinkOptions, webLink);
		await this.#dialogService.showAngularForm(dialogData, 400, 300);

	}

	async deleteWebLink(webLink: PersonWebLink) {

		const yes = await this.#dialogService.confirm(`Do you want to remove ${webLink.url} from ${this.person()?.general.fullName} ?`, 'Yes', 'No', [], 400, 200);

		if (yes) {
			await this.personExplorer.updateWebLink(webLink.webLinkId, webLink.webLinkTypeId, webLink.url, true);
		}
	}

}